/**
 * A Sim 9 round's end-of-round stats as TV summary sections (Cole,
 * 2026-09-30, on the Sim 9 challenge-sent card: "pop some end of round stats
 * up here instead of making me dig on phone"). The same facts the phone's
 * Round Stats overlay shows (screens/sim9/Sim9RoundStats.tsx): the scorecard,
 * AVG FIRST PUTT, the short/long split, the on-line rate, the first-putt
 * plot with hole numbers, and the start-line chart across every putt.
 *
 * Layout for 16:9: the header and the scorecard run across the top (`band`),
 * then the stats, the plot, and the chart side by side.
 *
 * Pure: colors and palettes come in as arguments, so node tests import it.
 */
import type { Sim9SubmittedPutt } from '../firebase';
import {
  DISTANCE_CONTROL_MIN_FT,
  ON_LINE_MAX_DEG,
  paceMissFt,
  startLineSide,
  tallyLine,
  tallyPace,
} from '../stats/rules';
import { DEFAULT_HOUSE_RINGS } from '../summaryVisuals/curlingHouse';
import { startLineChartPeak } from '../utils/startLineChartScale';
import type {
  TvSummaryEntriesSection,
  TvSummaryHouseSection,
  TvSummaryMatchBoardSection,
  TvSummarySection,
  TvSummaryStartLinesSection,
  TvSummaryText,
} from './summary';
import { tvText, type TvSummaryLayout } from './summarySections';

export interface Sim9RoundTvStats {
  /** Mean first-putt leave, a holed first putt as 0 ft; null with no putts. */
  avgFirstFt: number | null;
  /** "40% SHORT · 60% LONG" (first putts of 10 ft and longer), or null. */
  paceLine: string | null;
  /** "56% ON LINE (≤1.0°)" across every putt, or null. */
  onLineLine: string | null;
}

/** The overlay's numbers, computed the way Sim9RoundStats computes them. */
export function sim9RoundTvStats(holes: ReadonlyArray<readonly Sim9SubmittedPutt[]>): Sim9RoundTvStats {
  const firstPutts = holes.map((hole) => hole?.[0]).filter((putt): putt is Sim9SubmittedPutt => putt != null);
  const allPutts = holes.flatMap((hole) => [...(hole ?? [])]);
  const avgFirstFt = firstPutts.length
    ? firstPutts.reduce((sum, putt) => sum + (putt.holed ? 0 : Math.abs(putt.leaveFt)), 0) / firstPutts.length
    : null;
  const pace = tallyPace(
    firstPutts
      .filter((putt) => putt.requiredFt >= DISTANCE_CONTROL_MIN_FT)
      .map((putt) => ({ isMake: putt.holed, paceMissFt: paceMissFt(putt.distanceFt, putt.requiredFt) })),
  );
  const line = tallyLine(
    allPutts.map((putt) => ({
      side: startLineSide(
        Number.isFinite(putt.angleDeg as number) ? (putt.angleDeg as number) : null,
        putt.angleHighConfidence === true,
      ),
    })),
  );
  return {
    avgFirstFt,
    paceLine:
      pace.misses > 0 && pace.shortRate !== null
        ? `${Math.round(pace.shortRate * 100)}% SHORT · ${Math.round((pace.longRate ?? 0) * 100)}% LONG`
        : null,
    onLineLine:
      line.onLineRate !== null ? `${Math.round(line.onLineRate * 100)}% ON LINE (≤${ON_LINE_MAX_DEG.toFixed(1)}°)` : null,
  };
}

/** Lateral miss at the hole plane from the start line (Sim9RoundStats). */
function lateralMissFt(putt: Sim9SubmittedPutt): number {
  if (putt.holed || putt.angleDeg === null || !Number.isFinite(putt.angleDeg)) return 0;
  return Math.tan((putt.angleDeg * Math.PI) / 180) * putt.requiredFt;
}

export interface Sim9RoundTvInput {
  /** The header lines, top to bottom (the card's own text). */
  header: TvSummaryText[];
  /** Hole distances, feet. */
  holeDistancesFt: readonly number[];
  pars: readonly number[];
  /** Strokes per hole (null = not played). */
  strokes: ReadonlyArray<number | null>;
  /** Putts per hole in played order; index 0 of each hole is the first putt. */
  putts: ReadonlyArray<readonly Sim9SubmittedPutt[]>;
  /** Scorecard row label ("YOU", "@cole"). */
  playerLabel: string;
  playerColor: string;
  colors: { gold: string; textSecondary: string; textMuted: string; textPrimary?: string };
  palettes: {
    house: TvSummaryHouseSection['palette'];
    startLines: TvSummaryStartLinesSection['palette'];
    matchBoard: TvSummaryMatchBoardSection['palette'];
  };
  /** Ring labels in the player's unit. */
  unitPerFt: number;
  unitLabel: string;
  /** Formats a leave in the player's unit ("2.4 ft"). */
  formatFt: (ft: number) => string;
}

export function sim9RoundTvLayout(input: Sim9RoundTvInput): TvSummaryLayout {
  const { colors } = input;
  const stats = sim9RoundTvStats(input.putts);
  const firstPutts = input.putts.map((hole) => hole?.[0] ?? null);
  const balls = firstPutts.flatMap((putt, index) =>
    putt
      ? [{
          side: 'challenger' as const,
          order: index + 1,
          // The stats sign is + long; the house is + short.
          longitudinalMissFt: putt.holed ? 0 : -paceMissFt(putt.distanceFt, putt.requiredFt),
          lateralMissFt: lateralMissFt(putt),
          color: input.playerColor,
        }]
      : [],
  );
  const extentFt = Math.max(
    1,
    ...firstPutts.map((putt) => {
      if (!putt || putt.holed) return 0;
      const radius = Math.hypot(paceMissFt(putt.distanceFt, putt.requiredFt), lateralMissFt(putt) * 1.6);
      return Number.isFinite(radius) ? radius : 0;
    }),
  );
  const angles = input.putts.flatMap((hole) =>
    [...(hole ?? [])].map((putt) => (Number.isFinite(putt.angleDeg as number) ? (putt.angleDeg as number) : null)),
  );

  const sections: TvSummarySection[] = [
    { kind: 'text', band: 'top', column: 0, align: 'center', lines: input.header },
    {
      kind: 'matchBoard',
      band: 'top',
      column: 0,
      names: [input.playerLabel],
      holes: [...input.holeDistancesFt],
      pars: [...input.pars],
      strokesA: input.holeDistancesFt.map((_, i) => input.strokes[i] ?? null),
      strokesB: input.holeDistancesFt.map(() => null),
      palette: input.palettes.matchBoard,
    },
    {
      kind: 'text',
      column: 0,
      card: true,
      align: 'center',
      lines: [
        tvText('FIRST PUTTS', colors.textMuted, 'caption', 'bold', 0.15),
        tvText(stats.avgFirstFt === null ? '--' : input.formatFt(stats.avgFirstFt), colors.gold, 'value', 'heavy'),
        tvText('AVG FIRST PUTT', colors.textSecondary, 'caption', 'bold', 0.1),
        ...(stats.paceLine ? [tvText(stats.paceLine, colors.textSecondary, 'body', 'bold')] : []),
        ...(stats.onLineLine ? [tvText(stats.onLineLine, colors.textSecondary, 'body', 'bold')] : []),
      ],
    },
    balls.length
      ? {
          kind: 'house',
          column: 1,
          balls,
          rings: DEFAULT_HOUSE_RINGS.map((ring) => ({ ...ring })),
          lateralExaggeration: 1.6,
          headroomFactor: 1.2,
          edgePaddingPx: 28,
          minimumExtentFt: extentFt,
          showRingLabels: true,
          ringLabelPosition: 'top',
          unitPerFt: input.unitPerFt,
          unitLabel: input.unitLabel,
          palette: input.palettes.house,
          legend: [{ color: input.playerColor, text: 'FIRST PUTT ON EACH HOLE' }],
          legendHint: 'Numbers are hole numbers',
          legendColor: colors.textMuted,
        }
      : { kind: 'text', column: 1, align: 'center', lines: [tvText('No putts recorded.', colors.textMuted, 'body')] },
    {
      kind: 'startLines',
      column: 2,
      title: 'START LINES · ALL PUTTS',
      angles,
      palette: input.palettes.startLines,
    },
  ];
  return { sections, columns: [0.8, 1.1, 1] };
}

/* ---------------------------------------------------------------------------
 * Two or more players (2026-09-30): a Sim 9 local stroke-play round (up to
 * six) and a friend match answered on this phone. The header and one
 * scorecard with every player's row run across the top; then the standings
 * with each player's key numbers (strokes, first-putt average, short/long,
 * on line), the first-putt plot with one color per player, and, for two
 * players, each player's start-line chart. A match response also shows your
 * own round stats (`focusIndex`), the card the solo round gets.
 * ------------------------------------------------------------------------ */

export interface Sim9TvPlayer {
  /** Scorecard and standings name ("YOU", "@kelly", "Cole"). */
  label: string;
  color: string;
  /** Strokes per hole (null = not played). */
  strokes: ReadonlyArray<number | null>;
  /** Putts per hole in played order; index 0 of each hole is the first putt. */
  putts: ReadonlyArray<readonly Sim9SubmittedPutt[]>;
  /** Standings rank ("1", "T2"); null = unranked (joined mid-round). */
  rank?: string | null;
  /** The winner's gold border. */
  winner?: boolean;
}

export interface Sim9PlayersTvInput extends Omit<Sim9RoundTvInput, 'strokes' | 'putts' | 'playerLabel' | 'playerColor'> {
  players: readonly Sim9TvPlayer[];
  /** Whose round stats card to add (a match response: you). */
  focusIndex?: number | null;
  /** Standings title ("STANDINGS", "MATCH"). */
  standingsTitle?: string;
}

/** Round to 2 decimals: 54 balls must fit the 24 KB snapshot. */
const r2 = (value: number) => Math.round(value * 100) / 100;

function firstPuttBalls(
  putts: ReadonlyArray<readonly Sim9SubmittedPutt[]>,
  color: string,
  side: 'challenger' | 'opponent',
): TvSummaryHouseSection['balls'] {
  return putts.flatMap((hole, index) => {
    const putt = hole?.[0];
    if (!putt) return [];
    return [{
      side,
      order: index + 1,
      longitudinalMissFt: putt.holed ? 0 : r2(-paceMissFt(putt.distanceFt, putt.requiredFt)),
      lateralMissFt: r2(lateralMissFt(putt)),
      color,
    }];
  });
}

function firstPuttExtentFt(putts: ReadonlyArray<readonly Sim9SubmittedPutt[]>): number {
  return Math.max(
    0,
    ...putts.map((hole) => {
      const putt = hole?.[0];
      if (!putt || putt.holed) return 0;
      const radius = Math.hypot(paceMissFt(putt.distanceFt, putt.requiredFt), lateralMissFt(putt) * 1.6);
      return Number.isFinite(radius) ? radius : 0;
    }),
  );
}

function allAngles(putts: ReadonlyArray<readonly Sim9SubmittedPutt[]>): Array<number | null> {
  return putts.flatMap((hole) =>
    [...(hole ?? [])].map((putt) => (Number.isFinite(putt.angleDeg as number) ? r2(putt.angleDeg as number) : null)),
  );
}

function toParLabel(strokes: number, par: number): string {
  const diff = strokes - par;
  return diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`;
}

export function sim9PlayersTvLayout(input: Sim9PlayersTvInput): TvSummaryLayout {
  const { colors } = input;
  const players = input.players;
  if (players.length <= 1) {
    const only = players[0];
    return sim9RoundTvLayout({
      ...input,
      strokes: only?.strokes ?? [],
      putts: only?.putts ?? [],
      playerLabel: only?.label ?? 'YOU',
      playerColor: only?.color ?? colors.gold,
    });
  }
  const holes = input.holeDistancesFt.length;
  const strokeRow = (player: Sim9TvPlayer) => input.holeDistancesFt.map((_, i) => player.strokes[i] ?? null);
  const rows = players.map(strokeRow);

  const entries: TvSummaryEntriesSection['rows'] = players.map((player, index) => {
    const stats = sim9RoundTvStats(player.putts);
    const played = rows[index].map((value, i) => (value == null ? null : { value, par: input.pars[i] ?? 0 }))
      .filter((item): item is { value: number; par: number } => item !== null);
    const total = played.reduce((sum, item) => sum + item.value, 0);
    const par = played.reduce((sum, item) => sum + item.par, 0);
    const detail = [
      stats.avgFirstFt === null ? null : `${input.formatFt(stats.avgFirstFt)} avg first putt`,
      stats.paceLine?.toLowerCase() ?? null,
      stats.onLineLine?.replace(/ \(.*\)$/, '').toLowerCase() ?? null,
    ].filter(Boolean).join(' · ');
    return {
      rank: player.rank ?? null,
      rankColor: player.winner ? colors.gold : colors.textMuted,
      name: player.label,
      nameColor: player.color,
      detail: played.length < holes ? `${detail}${detail ? ' · ' : ''}${played.length} of ${holes} holes` : detail || null,
      detailColor: colors.textSecondary,
      value: played.length ? String(total) : '--',
      valueColor: player.winner ? colors.gold : colors.textPrimary ?? '#FFFFFF',
      valueLabel: played.length ? toParLabel(total, par) : null,
      border: player.winner ? colors.gold : null,
    };
  });

  const balls = players.flatMap((player, index) =>
    firstPuttBalls(player.putts, player.color, index === 0 ? 'challenger' : 'opponent'),
  );
  const extentFt = Math.max(1, ...players.map((player) => firstPuttExtentFt(player.putts)));

  const sections: TvSummarySection[] = [
    { kind: 'text', band: 'top', column: 0, align: 'center', lines: input.header },
    {
      kind: 'matchBoard',
      band: 'top',
      column: 0,
      names: players.map((player) => player.label),
      nameColors: players.map((player) => player.color),
      holes: [...input.holeDistancesFt],
      pars: [...input.pars],
      strokesA: rows[0],
      strokesB: rows[1],
      ...(players.length > 2 ? { strokesMore: rows.slice(2).flat() } : {}),
      palette: input.palettes.matchBoard,
    },
    { kind: 'entries', column: 0, title: input.standingsTitle ?? 'STANDINGS', rows: entries },
  ];

  const focus = input.focusIndex != null ? players[input.focusIndex] : null;
  if (focus) {
    const stats = sim9RoundTvStats(focus.putts);
    sections.push({
      kind: 'text',
      column: 0,
      card: true,
      align: 'center',
      lines: [
        tvText(`${focus.label.toUpperCase()} · FIRST PUTTS`, colors.textMuted, 'caption', 'bold', 0.15),
        tvText(stats.avgFirstFt === null ? '--' : input.formatFt(stats.avgFirstFt), colors.gold, 'title', 'heavy'),
        tvText('AVG FIRST PUTT', colors.textSecondary, 'caption', 'bold', 0.1),
        ...(stats.paceLine ? [tvText(stats.paceLine, colors.textSecondary, 'caption', 'bold')] : []),
        ...(stats.onLineLine ? [tvText(stats.onLineLine, colors.textSecondary, 'caption', 'bold')] : []),
      ],
    });
  }

  sections.push(
    balls.length
      ? {
          kind: 'house',
          column: 1,
          balls,
          rings: DEFAULT_HOUSE_RINGS.map((ring) => ({ ...ring })),
          lateralExaggeration: 1.6,
          headroomFactor: 1.2,
          edgePaddingPx: 28,
          minimumExtentFt: r2(extentFt),
          showRingLabels: true,
          ringLabelPosition: 'top',
          unitPerFt: input.unitPerFt,
          unitLabel: input.unitLabel,
          palette: input.palettes.house,
          legend: players.map((player) => ({ color: player.color, text: player.label.toUpperCase() })),
          legendHint: 'First putts. Numbers are hole numbers',
          legendColor: colors.textMuted,
        }
      : { kind: 'text', column: 1, align: 'center', lines: [tvText('No putts recorded.', colors.textMuted, 'body')] },
  );

  // A start-line chart per player fits two players; with more, the standings
  // carry each player's on-line rate instead.
  if (players.length === 2) {
    const angles = players.map((player) => allAngles(player.putts));
    // One count unit is the same height on both charts.
    const scaleMaxCount = Math.max(...angles.map((list) => startLineChartPeak(list)));
    for (const [index, player] of players.entries()) {
      sections.push({
        kind: 'startLines',
        column: 2,
        title: `START LINES · ${player.label.toUpperCase()}`,
        angles: angles[index],
        scaleMaxCount,
        palette: input.palettes.startLines,
      });
    }
    return { sections, columns: [1, 1.05, 0.9] };
  }
  return { sections, columns: [1.1, 1] };
}
